import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  FiPlus,
  FiEdit2,
  FiTrash2,
  FiEye,
  FiSearch,
  FiMoreVertical,
  FiCheck,
  FiX
} from 'react-icons/fi';
import { toast } from 'react-toastify';
import api from '../../services/api';

const AdminApartments = () => {
  const [apartments, setApartments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeMenu, setActiveMenu] = useState(null);

  useEffect(() => {
    fetchApartments();
  }, []);

  const fetchApartments = async () => {
    try {
      const data = await api.getApartments();
      setApartments(data.apartments || []);
    } catch (error) {
      console.error('Error fetching apartments:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this apartment?')) return;

    try {
      await api.deleteApartment(id);
      setApartments(apartments.filter(a => a._id !== id));
      toast.success('Apartment deleted successfully');
    } catch (error) {
      toast.error(error.message);
    }
  };

  const toggleStatus = async (apartment) => {
    try {
      await api.updateApartment(apartment._id, { isActive: !apartment.isActive });
      setApartments(apartments.map(a =>
        a._id === apartment._id ? { ...a, isActive: !a.isActive } : a
      ));
      toast.success(`Apartment ${!apartment.isActive ? 'activated' : 'deactivated'}`);
    } catch (error) {
      toast.error(error.message);
    }
  };

  const filteredApartments = apartments.filter(a =>
    a.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    a.location?.name?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const styles = {
    header: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: '24px'
    },
    title: {
      fontSize: '1.5rem',
      fontWeight: '700'
    },
    addBtn: {
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      padding: '10px 20px',
      background: '#1a3a5c',
      color: 'white',
      border: 'none',
      borderRadius: '8px',
      fontWeight: '500',
      cursor: 'pointer',
      textDecoration: 'none'
    },
    controls: {
      display: 'flex',
      gap: '16px',
      marginBottom: '24px'
    },
    searchWrapper: {
      flex: 1,
      position: 'relative'
    },
    searchIcon: {
      position: 'absolute',
      left: '14px',
      top: '50%',
      transform: 'translateY(-50%)',
      color: '#555'
    },
    searchInput: {
      width: '100%',
      padding: '12px 12px 12px 44px',
      border: '1px solid #ddd',
      borderRadius: '8px',
      fontSize: '0.9375rem'
    },
    card: {
      background: 'white',
      borderRadius: '12px',
      overflow: 'hidden',
      boxShadow: '0 2px 8px rgba(0,0,0,0.04)'
    },
    table: {
      width: '100%',
      borderCollapse: 'collapse'
    },
    th: {
      textAlign: 'left',
      padding: '16px',
      borderBottom: '1px solid #eee',
      color: '#555',
      fontWeight: '500',
      fontSize: '0.875rem'
    },
    td: {
      padding: '16px',
      borderBottom: '1px solid #f5f5f5',
      verticalAlign: 'middle'
    },
    apartmentInfo: {
      display: 'flex',
      alignItems: 'center',
      gap: '12px'
    },
    apartmentImage: {
      width: '60px',
      height: '45px',
      borderRadius: '6px',
      objectFit: 'cover'
    },
    apartmentName: {
      fontWeight: '500'
    },
    apartmentLocation: {
      color: '#555',
      fontSize: '0.8125rem'
    },
    badge: {
      padding: '4px 10px',
      borderRadius: '20px',
      fontSize: '0.75rem',
      fontWeight: '500'
    },
    badgeActive: {
      background: '#E8F5E9',
      color: '#2E7D32'
    },
    badgeInactive: {
      background: '#FFEBEE',
      color: '#C62828'
    },
    actions: {
      position: 'relative'
    },
    actionBtn: {
      padding: '8px',
      background: 'none',
      border: 'none',
      cursor: 'pointer',
      borderRadius: '4px'
    },
    dropdown: {
      position: 'absolute',
      right: 0,
      top: '100%',
      background: 'white',
      border: '1px solid #eee',
      borderRadius: '8px',
      boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
      zIndex: 10,
      minWidth: '150px'
    },
    dropdownItem: {
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      padding: '10px 16px',
      border: 'none',
      background: 'none',
      width: '100%',
      textAlign: 'left',
      cursor: 'pointer',
      fontSize: '0.875rem'
    },
    empty: {
      textAlign: 'center',
      padding: '60px',
      color: '#555'
    }
  };

  if (loading) {
    return <div className="loading-container"><div className="spinner" /></div>;
  }

  return (
    <div>
      <header style={styles.header}>
        <h1 style={styles.title}>Apartments</h1>
        <Link to="/admin/apartments/new" style={styles.addBtn}>
          <FiPlus /> Add Apartment
        </Link>
      </header>

      <div style={styles.controls}>
        <div style={styles.searchWrapper}>
          <FiSearch style={styles.searchIcon} />
          <input
            type="text"
            placeholder="Search apartments..."
            style={styles.searchInput}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
      </div>

      <div style={styles.card}>
        {filteredApartments.length > 0 ? (
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={styles.th}>Apartment</th>
                <th style={styles.th}>Type</th>
                <th style={styles.th}>Price/Night</th>
                <th style={styles.th}>Beds</th>
                <th style={styles.th}>Status</th>
                <th style={styles.th}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredApartments.map(apartment => (
                <tr key={apartment._id}>
                  <td style={styles.td}>
                    <div style={styles.apartmentInfo}>
                      <img
                        src={apartment.images?.[0] || '/images/placeholder-apartment.jpg'}
                        alt={apartment.name}
                        style={styles.apartmentImage}
                      />
                      <div>
                        <div style={styles.apartmentName}>{apartment.name}</div>
                        <div style={styles.apartmentLocation}>
                          {apartment.location?.name}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td style={styles.td}>{apartment.type}</td>
                  <td style={styles.td}>£{apartment.pricePerNight}</td>
                  <td style={styles.td}>{apartment.bedrooms}</td>
                  <td style={styles.td}>
                    <span
                      style={{
                        ...styles.badge,
                        ...(apartment.isActive ? styles.badgeActive : styles.badgeInactive)
                      }}
                    >
                      {apartment.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td style={styles.td}>
                    <div style={styles.actions}>
                      <button
                        style={styles.actionBtn}
                        onClick={() => setActiveMenu(activeMenu === apartment._id ? null : apartment._id)}
                      >
                        <FiMoreVertical />
                      </button>
                      {activeMenu === apartment._id && (
                        <div style={styles.dropdown}>
                          <Link
                            to={`/apartments/${apartment.slug}`}
                            style={{ ...styles.dropdownItem, textDecoration: 'none', color: 'inherit' }}
                            target="_blank"
                          >
                            <FiEye /> View
                          </Link>
                          <Link
                            to={`/admin/apartments/${apartment._id}/edit`}
                            style={{ ...styles.dropdownItem, textDecoration: 'none', color: 'inherit' }}
                          >
                            <FiEdit2 /> Edit
                          </Link>
                          <button
                            style={styles.dropdownItem}
                            onClick={() => toggleStatus(apartment)}
                          >
                            {apartment.isActive ? <FiX /> : <FiCheck />}
                            {apartment.isActive ? 'Deactivate' : 'Activate'}
                          </button>
                          <button
                            style={{ ...styles.dropdownItem, color: '#C62828' }}
                            onClick={() => handleDelete(apartment._id)}
                          >
                            <FiTrash2 /> Delete
                          </button>
                        </div>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <div style={styles.empty}>
            <p>No apartments found</p>
            <Link to="/admin/apartments/new" style={{ color: '#1a3a5c', marginTop: '12px', display: 'inline-block' }}>
              Add your first apartment
            </Link>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminApartments;
